import InputAdornment from '@mui/material/InputAdornment';
import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { formatNumber } from '@tempo-io/fim3-formulas';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from './CustomFieldValue';
import { StyledTextField } from './TextFieldInput';

export const NumberFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  isEditableText,
  ...rest
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
}) => {
  const { t } = useTranslation('translation');

  if (
    fieldConfiguration.fieldType !== 'NUMBER' &&
    fieldConfiguration.fieldType !== 'CURRENCY' &&
    fieldConfiguration.fieldType !== 'PERCENT'
  )
    return;

  let currencySymbol = null;
  if (fieldConfiguration.fieldType === 'CURRENCY') {
    currencySymbol = getCurrencySymbol(fieldConfiguration.currency);
  }

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues([event.target.value]);
  };

  const onBlur = () => {
    let value = values[0];
    if (value) {
      if (fieldConfiguration.fieldType === 'CURRENCY') {
        value = parseFloat(value).toFixed(2);
      }

      if (fieldConfiguration.places !== null && fieldConfiguration.places !== undefined) {
        value = formatNumber(value, fieldConfiguration.places);
      }
    }
    setValues(value ? [value].filter(Boolean) : []);
  };
  const numberInputOnWheelPreventChange = (e: React.WheelEvent<HTMLInputElement>) => {
    if (document.activeElement !== e.target) return;

    // Prevent the input value change
    (e.target as HTMLInputElement).blur();

    // Prevent the page/container scrolling
    e.preventDefault();
    e.stopPropagation();

    // Refocus immediately, on the next tick (after the current
    //function is done)
    setTimeout(() => {
      (e.target as HTMLInputElement).focus();
    }, 0);
  };

  return (
    <StyledTextField
      className="fs-exclude"
      data-dd-action-name="FIM 3 Custom Field - number field redacted"
      data-testid={'fim3-custom-field-input-number-input'}
      isEditableText={isEditableText}
      title="" // stops html validation from showing tooltip in the input field
      value={values[0] ?? ['']}
      onChange={onChange}
      onBlur={onBlur}
      onWheel={numberInputOnWheelPreventChange}
      slotProps={{
        htmlInput: {
          step: fieldConfiguration.increment,
          min: fieldConfiguration.minimum,
          max: fieldConfiguration.maximum,
          'data-testid': 'fim3-custom-field-input-number-input-field',
        },
        input: {
          startAdornment: currencySymbol ? (
            <InputAdornment position="start" data-testid="fim3-currency-adornment">
              {currencySymbol}
            </InputAdornment>
          ) : undefined,
        },
      }}
      type={'number'}
      placeholder={
        isEditableText ? undefined : t(($) => $.intake.general.numericFieldPlaceholder)
      }
      size="small"
      {...rest}
    />
  );
};
